'use client'

import type { SheetSize } from '@/components/ui/sheet'

import { useState } from 'react'
import { ArrowRightIcon, InsightsIcon, SidebarSimpleIcon } from '@/components/icons/icon-set'
import { Button } from '@/components/ui/button'
import { MetricCard } from '@/components/ui/metrics'
import { ChartCompact, ChartAcquisitionChannels, ChartRevenueOverview } from '@/components/blocks/chart-blocks'
import { AppShell, LayoutColumns, PageBody } from '@/components/blocks/app-layout'

import {
    AcquisitionMonthContent,
    ActionsFooterSelect,
    EditorContent,
    ExportFooterAction,
    FacebookDirectContent,
    RevenuePeopleContent
} from '@/components/blocks/drawer-blocks'

import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from '@/components/ui/alert-dialog'

import {
    Sheet,
    SheetBody,
    SheetClose,
    SheetContent,
    SheetFooter,
    SheetHeader
} from '@/components/ui/sheet'

/*
 * GoodUnited / Drawer templates. Figma: 31 / Templates / Drawers.
 * Drawers open from the right at full height. Header and footer stay fixed and only the body scrolls.
 * Focus stays inside, Escape closes and returns focus to where you were, and unsaved edits ask first.
 */
export type DrawerKind = 'empty-sm' | 'empty-default' | 'empty-lg' | 'revenue-people' | 'facebook-direct' | 'acquisition-month' | 'editor'

export const DRAWER_TEMPLATES: { kind: DrawerKind, title: string, description: string }[] = [
    { kind: 'empty-sm', title: 'Empty drawer, 480px', description: 'The sheet anatomy at the small width.' },
    { kind: 'empty-default', title: 'Empty drawer, 560px', description: 'The sheet anatomy at the default width.' },
    { kind: 'empty-lg', title: 'Empty drawer, 720px', description: 'The sheet anatomy at the large width.' },
    { kind: 'revenue-people', title: 'Revenue · People', description: 'Revenue by fundraiser, with a breakdown by source.' },
    { kind: 'facebook-direct', title: 'Facebook Direct', description: 'The people behind one source, with messages awaiting a reply.' },
    { kind: 'acquisition-month', title: 'January 2026', description: 'New supporters by acquisition channel.' },
    { kind: 'editor', title: 'Editor', description: 'Fundraiser page fields, preview and comments, with an unsaved changes prompt.' }
]

const sizes: Partial<Record<DrawerKind, SheetSize>> = {
    'empty-sm': 'sm',
    'empty-lg': 'lg'
}

const EDITOR_QUEUE = [
    { title: 'World Kidney Day', nonprofit: 'National Kidney Foundation' },
    { title: 'Spring Book Club', nonprofit: 'American Heart Association' },
    { title: 'Giving Tuesday Push', nonprofit: 'Feeding America' }
]

interface DrawerHostProps {
    drawer: DrawerKind | null
    onDrawerChange: (drawer: DrawerKind | null) => void
    editorTitle?: string
    editorDescription?: string
}

/* One Sheet whose content follows the requested drawer. Owns the unsaved changes prompt for the editor. */
export function DrawerHost({ drawer, onDrawerChange, editorTitle, editorDescription }: DrawerHostProps) {
    const [dirty, setDirty] = useState(false)
    const [pending, setPending] = useState<'close' | 'next' | null>(null)
    const [saveSignal, setSaveSignal] = useState(0)
    const [status, setStatus] = useState('Awaiting review')
    const [queueIndex, setQueueIndex] = useState(0)
    const current = EDITOR_QUEUE[queueIndex % EDITOR_QUEUE.length]
    const title = queueIndex === 0 && editorTitle ? editorTitle : current.title
    const description = queueIndex === 0 && editorDescription ? editorDescription : `Fundraiser page · ${current.nonprofit}`

    const finish = (action: 'close' | 'next') => {
        setDirty(false)
        setPending(null)
        if (action === 'close') {
            onDrawerChange(null)
            setQueueIndex(0)
        }
        else {
            setQueueIndex(index => index + 1)
        }
    }

    const request = (action: 'close' | 'next') => {
        if (drawer === 'editor' && dirty) {
            setPending(action)
            return
        }
        finish(action)
    }

    // Keep the last drawer's content while the sheet animates closed.
    const [shown, setShown] = useState<DrawerKind>(drawer ?? 'empty-default')
    if (drawer && drawer !== shown) setShown(drawer)
    const kind = drawer ?? shown

    return (
        <Sheet open={drawer !== null} onOpenChange={open => !open && request('close')}>
            <SheetContent size={sizes[kind] ?? 'default'}>
                {(kind === 'empty-sm' || kind === 'empty-default' || kind === 'empty-lg') && (
                    <>
                        <SheetHeader title='Drawer title' description='Content for the selected item.' />
                        <SheetBody />
                        <SheetFooter>
                            <SheetClose render={<Button variant='outline' />}>
                                Cancel
                            </SheetClose>
                            <Button onClick={() => onDrawerChange(null)}>
                                Save changes
                            </Button>
                        </SheetFooter>
                    </>
                )}
                {kind === 'revenue-people' && (
                    <>
                        <SheetHeader title='Revenue · People' description='Revenue by fundraiser' />
                        <SheetBody>
                            <RevenuePeopleContent />
                        </SheetBody>
                        <SheetFooter>
                            <ExportFooterAction />
                        </SheetFooter>
                    </>
                )}
                {kind === 'facebook-direct' && (
                    <>
                        <SheetHeader title='Facebook Direct' description='People behind the selected source' onBack={() => onDrawerChange('acquisition-month')} />
                        <SheetBody>
                            <FacebookDirectContent />
                        </SheetBody>
                        <SheetFooter>
                            <ExportFooterAction />
                        </SheetFooter>
                    </>
                )}
                {kind === 'acquisition-month' && (
                    <>
                        <SheetHeader title='January 2026' description='New supporters by acquisition channel' />
                        <SheetBody>
                            <AcquisitionMonthContent />
                            <Button variant='link' className='self-start' onClick={() => onDrawerChange('facebook-direct')}>
                                See the people behind Facebook Direct
                                <ArrowRightIcon aria-hidden='true' />
                            </Button>
                        </SheetBody>
                        <SheetFooter>
                            <ExportFooterAction />
                        </SheetFooter>
                    </>
                )}
                {kind === 'editor' && (
                    <>
                        <SheetHeader
                            title={title}
                            description={description}
                            actions={(
                                <Button variant='outline' size='sm' onClick={() => request('next')}>
                                    Next action
                                </Button>
                            )}
                        />
                        <SheetBody>
                            <EditorContent key={title} defaultValues={{ pageName: title }} saveSignal={saveSignal} onDirtyChange={setDirty} />
                        </SheetBody>
                        <SheetFooter className='flex-wrap justify-between'>
                            <div className='min-w-0 flex-1'>
                                <ActionsFooterSelect value={status} onValueChange={setStatus} />
                            </div>
                            {dirty && (
                                <Button onClick={() => setSaveSignal(signal => signal + 1)}>
                                    Save changes
                                </Button>
                            )}
                        </SheetFooter>
                    </>
                )}

                <AlertDialog open={pending !== null} onOpenChange={open => !open && setPending(null)}>
                    <AlertDialogContent>
                        <AlertDialogHeader tone='warning'>
                            <AlertDialogTitle>
                                Discard unsaved changes?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                Your edits to this page have not been saved. If you leave now they are lost.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <Button variant='outline' onClick={() => setPending(null)}>
                                Keep editing
                            </Button>
                            <Button variant='destructive' onClick={() => finish(pending ?? 'close')}>
                                Discard changes
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </SheetContent>
        </Sheet>
    )
}

/* The dashboard every drawer opens over. Each metric opens its drawer. */
function DrawerDashboard({ onOpen, openLabel }: { onOpen: (drawer: DrawerKind) => void, openLabel?: { kind: DrawerKind, label: string } }) {
    const metric = (kind: DrawerKind, label: string) => (
        <Button variant='outline' size='icon-sm' onClick={() => onOpen(kind)} aria-label={`Open ${label}`}>
            <ArrowRightIcon aria-hidden='true' />
        </Button>
    )

    return (
        <AppShell title='Insights' icon={<InsightsIcon />} activeItem='insights' showDate>
            <PageBody
                title='Insights'
                description='Revenue, supporters and actions for January 2026'
                actions={openLabel && (
                    <Button variant='outline' onClick={() => onOpen(openLabel.kind)}>
                        <SidebarSimpleIcon mirrored aria-hidden='true' />
                        {openLabel.label}
                    </Button>
                )}
            >
                <LayoutColumns track='4'>
                    <MetricCard label='Revenue' value='$54,000' accent='positive' actions={metric('revenue-people', 'revenue by fundraiser')} />
                    <MetricCard label='New supporters' value='350' accent='platform' actions={metric('acquisition-month', 'new supporters by channel')} />
                    <MetricCard label='New subscribers' value='640' accent='facebook' actions={metric('facebook-direct', 'Facebook Direct people')} />
                    <MetricCard label='Awaiting review' value='3' accent='challenges' actions={metric('editor', 'the next action to review')} />
                </LayoutColumns>
                <ChartRevenueOverview />
                <LayoutColumns track='3'>
                    <ChartCompact variant='area' className='max-w-none' />
                    <ChartCompact variant='line' className='max-w-none' />
                    <ChartAcquisitionChannels className='max-w-none' />
                </LayoutColumns>
            </PageBody>
        </AppShell>
    )
}

/* Template/Drawer: the dashboard with the chosen drawer open over it. */
export function DrawerTemplate({ kind, defaultOpen = true }: { kind: DrawerKind, defaultOpen?: boolean }) {
    const [drawer, setDrawer] = useState<DrawerKind | null>(defaultOpen ? kind : null)
    const empty = kind.startsWith('empty')

    return (
        <>
            <DrawerDashboard onOpen={setDrawer} openLabel={empty ? { kind, label: 'Open drawer' } : undefined} />
            <DrawerHost drawer={drawer} onDrawerChange={setDrawer} />
        </>
    )
}
